import ShortlistIcon from "icons/ShortlistIcon";
import styles from "./ShortListButton.module.scss";
import { useState, MouseEventHandler, useEffect } from "react";
import { AppState } from "@Store/index";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import LoginDispatcher from "@Components/Login/actions";
import themeVariables from "@Theme/exportColor.module.scss";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import createInventoriesShortlist, {
  deleteInventoriesShortlist,
  addShortlistParamInURL,
} from "./helper";
import LoaderIcon from "@Icons/LoaderIcon";
import ToastNotificationDispatcher from "@Components/ToastNotification/actions";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { updateUserData } from "@App/appReducer";
import ActivityPageDispatcher from "@Pages/Activity/data/actions";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { getAvailableStartingPricingHelper } from "@Pages/BookForm/utils";
import { getPricingCurrencyStandardName } from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import { logAppEvent } from "@Utils/appAnalytics";
import useIsApp from "hooks/useIsApp";
import { APP_EVENTS } from "@Utils/webApputils/analyticEvents";

const ShortlistButton = ({
  dataTestId,
  inventoryId,
  inventory,
  noAbosolute = false,
  withText = false,
  topRight = false,
  shortlistedCardID,
  className = "",
  wrapperClassName = "",
  screenName = "",
  iconColor,
}: {
  inventoryId: number;
  noAbosolute?: boolean;
  inventory: any;
  withText?: boolean;
  topRight?: boolean;
  dataTestId?: string;
  shortlistedCardID?: any;
  className?: string;
  wrapperClassName?: string;
  screenName?: string;
  iconColor?: string;
}): JSX.Element => {
  const {
    isUserLoggedIn,
    userData,
    shortlistedActivityPage,
    shortlistedCount,
    shortlistedPageCount,
  } = useSelector(
    ({ appData, activityPageData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      userData: appData.userData,
      shortlistedActivityPage: activityPageData.shortlisted,
      shortlistedCount: activityPageData.shortlistedCount,
      shortlistedPageCount: activityPageData.shortlistedPageCount,
    }),
    shallowEqual,
  );

  const isApp = useIsApp();
  const [shortlisted, setShortlisted] = useState({
    isShortlisted: false,
    componentState: "inactive",
  });

  const { t } = useTranslationHook();

  useEffect(() => {
    if (userData) {
      const isListed = userData?.data?.inventories_shortlists_ids.includes(inventoryId);
      setShortlisted({ isShortlisted: isListed, componentState: isListed ? "active" : "inactive" });
    }
  }, [JSON.stringify(userData), inventoryId]);

  const dispatch = useDispatch();
  const activityPageDispatcher = new ActivityPageDispatcher(dispatch);
  // maintaining two different functions here because shortlisting fires more events
  const shortlistInventoryHandler: MouseEventHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isUserLoggedIn) {
      setShortlisted({ isShortlisted: shortlisted.isShortlisted, componentState: "loading" });
      const options = {
        url_path: "/api/v0/auth/users/inventories_shortlists/",
        data: {
          inventories_shortlist: { inventory_id: inventoryId },
        },
      };
      const res = await createInventoriesShortlist(options);
      if (res) {
        // update userdata as soon as shortlist is done
        let tempUserData: any = JSON.stringify(userData);
        tempUserData = JSON.parse(tempUserData);
        tempUserData.data.inventories_shortlists_ids = [
          ...tempUserData.data.inventories_shortlists_ids,
          inventoryId,
        ];
        dispatch(
          updateUserData({
            status: true,
            data: tempUserData,
          }),
        );
        if (shortlistedActivityPage) {
          let updatedshortlistedActivityPage: any = [];

          if (inventory) updatedshortlistedActivityPage = [inventory, ...shortlistedActivityPage];
          activityPageDispatcher.updateShortlistedList(updatedshortlistedActivityPage);
        }
        setShortlisted({ isShortlisted: true, componentState: "active" });
        const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
        toastNotificationDispatcher.openToast({
          title: t("propertypage.propertyheader.shortlist.addedtitle"),
          message: t("propertypage.propertyheader.shortlist.addedmessage"),
        });
      }
    } else {
      addShortlistParamInURL(inventoryId);
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({
        openingSource: "shortlist",
        ...(isApp && { screenName }),
      });
    }
    // @ts-ignore
    if (typeof fbq !== "undefined" && fbq !== null) {
      // place shortlist event
      // @ts-ignore
      fbq("track", "place_shortlist", { inventory_id: inventoryId });
    }
    gtagEventEmitter({
      action: "place_shortlist",
      category: "search",
      label: "success",
    });
    logAppEvent({
      eventName: APP_EVENTS.PROPERTY_WISHLISTED,
      payload: {
        property_id: inventory?.id,
        property_name: inventory?.name,
        currency: getPricingCurrencyStandardName(inventory?.pricing?.currency),
        value: getAvailableStartingPricingHelper(inventory?.pricing),
        page: "search",
      },
    });
  };

  const removeShortlistInventoryHandler: MouseEventHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUserLoggedIn) {
      setShortlisted({ isShortlisted: shortlisted.isShortlisted, componentState: "loading" });
      const options = {
        url_path: "/api/v0/auth/users/inventories_shortlists/",
        data: {
          inventories_shortlist: { inventory_id: inventoryId },
        },
      };
      const res = await deleteInventoriesShortlist(inventoryId, options);
      if (res) {
        // update userdata as soon as shortlist is done
        const tempUserDataStringified: string = JSON.stringify(userData);
        let tempUserData = JSON.parse(tempUserDataStringified);
        const index = tempUserData.data.inventories_shortlists_ids.indexOf(inventoryId);
        if (index > -1) {
          tempUserData = {
            ...tempUserData,
            data: {
              ...tempUserData?.data,
              inventories_shortlists_ids: tempUserData?.data?.inventories_shortlists_ids?.filter(
                (id: any) => id !== inventoryId,
              ),
            },
          };
        }
        dispatch(
          updateUserData({
            status: true,
            data: tempUserData,
          }),
        );
        if (shortlistedActivityPage) {
          let updatedshortlistedActivityPage = [];
          updatedshortlistedActivityPage = shortlistedActivityPage.filter(
            (inv: any) => inv.id !== inventoryId,
          );
          activityPageDispatcher.updateShortlistedList(updatedshortlistedActivityPage);
        }

        setShortlisted({ isShortlisted: false, componentState: "inactive" });
      }
      const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
      toastNotificationDispatcher.openToast({
        title: t("propertypage.propertyheader.shortlist.removedtitle"),
        message: t("propertypage.propertyheader.shortlist.removedmessage"),
      });
      // @ts-ignore
      gtagEventEmitter({
        action: "remove_shortlist",
        category: "search",
        label: "success",
      });
    } else {
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "shortlist", ...(isApp && { screenName }) });
    }
  };

  const removeShortlistInventoryHandlerForShortlistPage: MouseEventHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUserLoggedIn) {
      setShortlisted({ isShortlisted: shortlisted.isShortlisted, componentState: "loading" });
      const options = {
        url_path: "/api/v0/auth/users/inventories_shortlists/",
        data: {
          inventories_shortlist: { inventory_id: inventoryId },
        },
      };
      const res = await deleteInventoriesShortlist(inventoryId, options);
      if (res) {
        // update userdata as soon as shortlist is done
        const tempUserDataStringified: string = JSON.stringify(userData);
        let tempUserData = JSON.parse(tempUserDataStringified);
        const index = tempUserData.data.inventories_shortlists_ids.indexOf(inventoryId);
        if (index > -1) {
          tempUserData = {
            ...tempUserData,
            data: {
              ...tempUserData?.data,
              inventories_shortlists_ids: tempUserData?.data?.inventories_shortlists_ids?.filter(
                (id: any) => id !== inventoryId,
              ),
            },
          };
        }
        dispatch(
          updateUserData({
            status: true,
            data: tempUserData,
          }),
        );
        if (shortlistedActivityPage) {
          let updatedshortlistedActivityPage = [];
          updatedshortlistedActivityPage = shortlistedActivityPage.filter(
            (inv: any) => inv.id !== shortlistedCardID,
          );
          activityPageDispatcher.updateShortlistedList(updatedshortlistedActivityPage);
          activityPageDispatcher.updateShortlistedListCount(shortlistedCount - 1);
          if ((shortlistedCount - 1) % 10 === 0) {
            activityPageDispatcher.updateShortlistedPageCount(shortlistedPageCount - 1);
          }
        }
      }
      const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
      toastNotificationDispatcher.openToast({
        title: t("propertypage.propertyheader.shortlist.removedtitle"),
        message: t("propertypage.propertyheader.shortlist.removedmessage"),
      });
      // @ts-ignore
      gtagEventEmitter({
        action: "remove_shortlist",
        category: "search",
        label: "success",
      });
    } else {
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "shortlist", ...(isApp && { screenName }) });
    }
  };
  const handleAction = (e: any) => {
    if (e.type === "keydown") {
      if (e.key !== "Enter" && e.key !== " ") {
        return;
      }
    }

    if (shortlistedCardID === 0) {
      if (shortlisted.isShortlisted) {
        removeShortlistInventoryHandler(e);
      } else {
        shortlistInventoryHandler(e);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (shortlisted.isShortlisted) {
        removeShortlistInventoryHandlerForShortlistPage(e);
      } else {
        shortlistInventoryHandler(e);
      }
    }
  };

  return (
    <div
      className={formatClassNames(styles.container, wrapperClassName)}
      role="button"
      tabIndex={0}
      onKeyDown={(e: any) => handleAction(e)}
      onClick={(e: any) => handleAction(e)}
    >
      <div
        className={formatClassNames(
          styles.shortlistButton,
          styles[shortlisted.componentState],
          noAbosolute && styles.noAbosolute,
          withText && styles.withText,
          topRight && styles.topRight,
          className && className,
        )}
        data-testid={dataTestId}
      >
        {shortlisted.componentState === "loading" ? (
          <LoaderIcon />
        ) : (
          <ShortlistIcon
            fill={shortlisted.isShortlisted ? "white" : iconColor || themeVariables.neutral5}
          />
        )}
      </div>
      {withText && (
        <div className={styles.text}>
          {shortlisted.isShortlisted
            ? t("propertypage.propertyheader.shortlist.shortlisted")
            : t("propertypage.propertyheader.shortlist.shortlist")}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(ShortlistButton, {
  componentName: "ShortlistButton-SearchListItemDesktop-SearchPage",
  showForChina: true,
});
